/**
 * Giving by month (per-month giving, `Funds\Modules\MonthsModule` in its dated mode). The donor chooses how to give: specific
 * months paid once, a gift that repeats every month, or both. This collects the choice and shows the server's figures; the server
 * resolves every month's amount and the total (rule 2), checks the window again when the payment is opened, and sends the figures
 * back with each quote.
 *
 * The month grid folds earlier and later years (only the current year is open), so 36 or 60 months back is never a wall of
 * buttons. A chosen month shows a check mark as well as a color. Every control is at least 44 px tall (CSS).
 *
 * Nothing here is written to the draft: a month list is cheap to choose again, and a stale one (a window that moved overnight) is a
 * worse thing to restore than an empty one.
 */

const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

/**
 * The day of a server date (`YYYY-MM-DD`) as an ordinal: "1st", "2nd", "24th". English, as the sentence it goes in is written.
 *
 * @param {string} date The date.
 * @return {string}
 */
function ordinalDay( date ) {
	const day = parseInt( String( date || '' ).split( '-' )[ 2 ], 10 );

	if ( ! ( day > 0 ) ) {
		return '';
	}

	const suffixes = { one: 'st', two: 'nd', few: 'rd', other: 'th' };

	return `${ day }${ suffixes[ new Intl.PluralRules( 'en-US', { type: 'ordinal' } ).select( day ) ] || 'th' }`;
}

/**
 * "2026-10" as month and year numbers.
 *
 * @param {string} month `YYYY-MM`.
 * @return {{year: number, month: number}}
 */
function parts( month ) {
	const [ year, number ] = month.split( '-' ).map( ( piece ) => parseInt( piece, 10 ) );

	return { year, month: number };
}

/**
 * Every month from one to another, inclusive.
 *
 * @param {string} from `YYYY-MM`.
 * @param {string} to   `YYYY-MM`.
 * @return {string[]}
 */
export function monthRange( from, to ) {
	const out = [];
	let { year, month } = parts( from );
	const end = parts( to );

	while ( year < end.year || ( year === end.year && month <= end.month ) ) {
		out.push( `${ year }-${ String( month ).padStart( 2, '0' ) }` );
		month += 1;

		if ( month > 12 ) {
			month = 1;
			year += 1;
		}
	}

	return out;
}

/**
 * The month as a person reads it: "October 2026", in the page's language.
 *
 * @param {string} month `YYYY-MM`.
 * @param {string} style `long` or `short`.
 * @return {string}
 */
export function monthLabel( month, style = 'long' ) {
	const { year, month: number } = parts( month );
	const date = new Date( Date.UTC( year, number - 1, 15, 12 ) );
	const locale = document.documentElement.lang || undefined;

	try {
		return new Intl.DateTimeFormat( locale, { month: style, year: 'long' === style ? 'numeric' : undefined, timeZone: 'UTC' } ).format( date );
	} catch ( error ) {
		return month;
	}
}

export class MonthsUi {
	/**
	 * @param {Object} wizard The checkout wizard (its `buildAmounts()`, formatting and summary).
	 */
	constructor( wizard ) {
		this.wizard = wizard;
		this.schema = wizard.form.months || {};
		this.modes = Array.isArray( this.schema.modes ) && this.schema.modes.length ? this.schema.modes : [ 'specific' ];
		this.mode = this.modes[ 0 ];
		this.selected = [];
		this.different = false;
		this.openYears = new Set( [ parts( this.schema.current || this.schema.earliest ).year ] );
		this.buttons = new Map();
		this.rows = new Map();
		// "Both": the monthly gift that begins after the months has its own amount.
		this.monthlyTierId = null;
		this.monthlyCustom = null;
	}

	/**
	 * The section.
	 *
	 * @return {HTMLElement}
	 */
	build() {
		const section = document.createElement( 'div' );

		section.className = 'anudan-checkout__dated-months';

		if ( this.modes.length > 1 ) {
			section.appendChild( this.buildModes() );
		}

		this.monthsPanel = document.createElement( 'div' );
		this.monthsPanel.className = 'anudan-checkout__dated-panel anudan-checkout__dated-panel--months';
		this.monthsPanel.appendChild( this.buildGrid() );
		section.appendChild( this.monthsPanel );

		this.amountPanel = document.createElement( 'div' );
		this.amountPanel.className = 'anudan-checkout__dated-panel anudan-checkout__dated-panel--amount';

		this.amountHeading = document.createElement( 'span' );
		this.amountHeading.className = 'anudan-checkout__field-label';
		this.amountPanel.appendChild( this.amountHeading );
		this.amountHolder = this.wizard.buildAmounts();
		this.amountPanel.appendChild( this.amountHolder );
		section.appendChild( this.amountPanel );

		if ( false !== this.schema.allow_different ) {
			this.differentPanel = this.buildDifferentToggle();
			section.appendChild( this.differentPanel );
		}

		this.listPanel = document.createElement( 'div' );
		this.listPanel.className = 'anudan-checkout__dated-list';
		this.listPanel.setAttribute( 'aria-live', 'polite' );
		section.appendChild( this.listPanel );

		this.monthlyPanel = this.buildMonthlyPanel();
		section.appendChild( this.monthlyPanel );

		this.errors = document.createElement( 'p' );
		this.errors.className = 'anudan-checkout__field-error anudan-checkout__dated-errors';
		this.errors.setAttribute( 'role', 'alert' );
		this.errors.hidden = true;
		section.appendChild( this.errors );

		this.sync();

		return section;
	}

	/**
	 * The choice of how to give: real radio buttons.
	 *
	 * @return {HTMLElement}
	 */
	buildModes() {
		const names = {
			specific: [ __( 'Specific months' ), __( 'Choose the months you are paying for and pay once.' ) ],
			every: [ __( 'Every month' ), __( 'A gift that repeats. Each payment counts for the month it is due.' ) ],
			both: [ __( 'Both' ), __( 'Pay for months now, and a monthly gift starts after them.' ) ],
		};
		const group = document.createElement( 'div' );

		group.className = 'anudan-checkout__dated-modes';
		group.setAttribute( 'role', 'radiogroup' );
		group.setAttribute( 'aria-label', __( 'How would you like to give?' ) );

		this.modes.forEach( ( mode ) => {
			const label = document.createElement( 'label' );
			const input = document.createElement( 'input' );
			const title = document.createElement( 'span' );
			const help = document.createElement( 'span' );

			label.className = 'anudan-checkout__dated-mode';
			input.type = 'radio';
			input.name = `anudan-month-mode-${ this.wizard.fundId }`;
			input.value = mode;
			input.checked = mode === this.mode;
			input.addEventListener( 'change', () => this.setMode( mode ) );
			title.className = 'anudan-checkout__dated-mode-title';
			title.textContent = ( names[ mode ] || [ mode ] )[ 0 ];
			help.className = 'anudan-checkout__dated-mode-help';
			help.textContent = ( names[ mode ] || [ '', '' ] )[ 1 ];
			label.appendChild( input );
			label.appendChild( title );
			label.appendChild( help );
			group.appendChild( label );
		} );

		return group;
	}

	/**
	 * Switches the way of giving.
	 *
	 * @param {string} mode `specific`, `every` or `both`.
	 * @return {void}
	 */
	setMode( mode ) {
		this.mode = mode;
		this.sync();
		this.wizard.updateSummary();
	}

	/**
	 * The month grid, one group per year; the current year open, the others folded.
	 *
	 * @return {HTMLElement}
	 */
	buildGrid() {
		const wrapper = document.createElement( 'div' );
		const heading = document.createElement( 'span' );
		const years = new Map();

		wrapper.className = 'anudan-checkout__dated-grid';
		heading.className = 'anudan-checkout__field-label';
		heading.id = `${ this.wizard.idPrefix }-months-label`;
		heading.textContent = __( 'Which months are you paying for?' );
		wrapper.appendChild( heading );

		monthRange( this.schema.earliest, this.schema.latest ).forEach( ( month ) => {
			const { year } = parts( month );

			if ( ! years.has( year ) ) {
				years.set( year, [] );
			}

			years.get( year ).push( month );
		} );

		years.forEach( ( months, year ) => {
			const details = document.createElement( 'details' );
			const summary = document.createElement( 'summary' );
			const group = document.createElement( 'div' );

			details.className = 'anudan-checkout__dated-year';
			details.open = this.openYears.has( year );
			details.addEventListener( 'toggle', () => {
				if ( details.open ) {
					this.openYears.add( year );
				} else {
					this.openYears.delete( year );
				}
			} );

			summary.className = 'anudan-checkout__dated-year-summary';
			summary.dataset.year = String( year );
			details.appendChild( summary );

			group.className = 'anudan-checkout__dated-months-grid';
			group.setAttribute( 'role', 'group' );
			group.setAttribute( 'aria-label', String( year ) );

			months.forEach( ( month ) => {
				const button = document.createElement( 'button' );

				button.type = 'button';
				button.className = 'anudan-checkout__dated-month';
				button.dataset.month = month;
				button.setAttribute( 'aria-pressed', 'false' );
				button.setAttribute( 'aria-label', monthLabel( month ) );
				button.addEventListener( 'click', () => this.toggle( month ) );

				const name = document.createElement( 'span' );

				name.textContent = monthLabel( month, 'short' );
				button.appendChild( name );

				const mark = document.createElement( 'span' );

				mark.className = 'anudan-checkout__dated-check';
				mark.setAttribute( 'aria-hidden', 'true' );
				mark.textContent = '✓';
				button.appendChild( mark );

				this.buttons.set( month, button );
				group.appendChild( button );
			} );

			details.appendChild( group );
			wrapper.appendChild( details );
			this.yearSummaries = this.yearSummaries || new Map();
			this.yearSummaries.set( year, { summary, months } );
		} );

		return wrapper;
	}

	/**
	 * The monthly gift that begins after the months ("both"): its own amount from the fund's presets or a custom amount, and what the
	 * server says about when it starts. Shown only in that way of giving.
	 *
	 * @return {HTMLElement}
	 */
	buildMonthlyPanel() {
		const panel = document.createElement( 'div' );
		const heading = document.createElement( 'span' );
		const group = document.createElement( 'div' );
		const name = `anudan-monthly-amount-${ this.wizard.fundId }`;

		panel.className = 'anudan-checkout__dated-panel anudan-checkout__dated-panel--monthly';
		heading.className = 'anudan-checkout__field-label';
		heading.textContent = __( 'Then every month, starting after these months' );
		panel.appendChild( heading );

		group.className = 'anudan-checkout__amounts';
		group.setAttribute( 'role', 'radiogroup' );
		group.setAttribute( 'aria-label', __( 'Choose the monthly amount' ) );

		( this.wizard.form.amounts || [] ).forEach( ( tier ) => {
			const label = document.createElement( 'label' );
			const input = document.createElement( 'input' );
			const value = document.createElement( 'span' );

			label.className = 'anudan-checkout__amount';
			input.type = 'radio';
			input.name = name;
			input.value = String( tier.id );
			input.addEventListener( 'change', () => {
				this.monthlyTierId = tier.id;
				this.monthlyCustom = null;

				if ( this.monthlyInput ) {
					this.monthlyInput.value = '';
				}

				this.wizard.updateSummary();
			} );
			value.className = 'anudan-checkout__amount-value';
			value.textContent = this.wizard.formatMinorUnits( tier.amount );
			label.appendChild( input );
			label.appendChild( value );
			group.appendChild( label );
		} );

		panel.appendChild( group );

		if ( this.wizard.form.allow_custom_amount ) {
			const field = document.createElement( 'div' );
			const label = document.createElement( 'label' );
			const money = document.createElement( 'div' );
			const symbol = document.createElement( 'span' );
			const currency = this.wizard.form.currency || 'USD';

			field.className = 'anudan-checkout__field';
			label.className = 'anudan-checkout__field-label';
			label.htmlFor = `${ name }-custom`;
			label.textContent = __( 'Or enter your own monthly amount, in %s' ).replace( '%s', currency );
			this.monthlyInput = document.createElement( 'input' );
			this.monthlyInput.type = 'text';
			this.monthlyInput.id = `${ name }-custom`;
			this.monthlyInput.inputMode = 'decimal';
			this.monthlyInput.className = 'anudan-checkout__field-input anudan-checkout__custom-amount';
			this.monthlyInput.addEventListener( 'input', () => {
				this.monthlyTierId = null;
				this.monthlyCustom = this.wizard.parseMinorUnits( this.monthlyInput.value );
				group.querySelectorAll( 'input[type="radio"]' ).forEach( ( radio ) => {
					radio.checked = false;
				} );
				this.wizard.updateSummary();
			} );
			money.className = 'anudan-checkout__money';
			symbol.className = 'anudan-checkout__money-symbol';
			symbol.setAttribute( 'aria-hidden', 'true' );
			symbol.textContent = this.wizard.currencySymbol( currency );
			money.appendChild( symbol );
			money.appendChild( this.monthlyInput );
			field.appendChild( label );
			field.appendChild( money );
			panel.appendChild( field );
		}

		this.monthlyInfo = document.createElement( 'p' );
		this.monthlyInfo.className = 'anudan-checkout__dated-notice anudan-checkout__dated-monthly-info';
		this.monthlyInfo.setAttribute( 'role', 'status' );
		this.monthlyInfo.hidden = true;
		panel.appendChild( this.monthlyInfo );

		this.skippedBox = document.createElement( 'p' );
		this.skippedBox.className = 'anudan-checkout__dated-notice anudan-checkout__dated-skipped';
		this.skippedBox.hidden = true;
		panel.appendChild( this.skippedBox );

		return panel;
	}

	/**
	 * Says when the monthly gift starts and, gently, offers the months between the last one chosen and now (never adds them).
	 *
	 * @param {Object|undefined} monthly The quote's `monthly`: `start_month`, `first_payment_date`, `immediate`, `skipped`.
	 * @return {void}
	 */
	showMonthly( monthly ) {
		const on = 'both' === this.mode && monthly && monthly.start_month;

		this.monthlyInfo.hidden = ! on;
		this.skippedBox.hidden = true;
		this.skippedBox.textContent = '';

		if ( ! on ) {
			return;
		}

		// R9 (0.9.40, Phase 3b): "Then $25.00 every month from January 2027, on the 24th. Change or cancel anytime." The month is the
		// one the first payment counts for and the day the day it is taken, both the server's (Subscriptions\MonthlyStart).
		const start = monthLabel( monthly.start_month );
		const day = ordinalDay( monthly.first_payment_date );
		const amount = 'number' === typeof monthly.amount ? this.wizard.formatMinorUnits( monthly.amount ) : '';
		const text = amount
			/* translators: 1: the monthly amount, e.g. $25.00, 2: a month, e.g. January 2027, 3: a day of the month, e.g. 24th. */
			? __( 'Then %1$s every month from %2$s, on the %3$s. Change or cancel anytime.' ).replace( '%1$s', amount ).replace( '%2$s', start ).replace( '%3$s', day )
			/* translators: 1: a month, e.g. January 2027, 2: a day of the month, e.g. 24th. */
			: __( 'Then a monthly gift from %1$s, on the %2$s. Change or cancel anytime.' ).replace( '%1$s', start ).replace( '%2$s', day );

		this.monthlyInfo.textContent = text;

		const skipped = Array.isArray( monthly.skipped ) ? monthly.skipped : [];

		if ( ! skipped.length ) {
			return;
		}

		const range = 1 === skipped.length
			? monthLabel( skipped[ 0 ] )
			: __( '%1$s to %2$s' ).replace( '%1$s', monthLabel( skipped[ 0 ] ) ).replace( '%2$s', monthLabel( skipped[ skipped.length - 1 ] ) );
		const add = document.createElement( 'button' );

		add.type = 'button';
		add.className = 'anudan-checkout__dated-add-skipped';
		add.textContent = __( 'Add them' );
		add.addEventListener( 'click', () => this.addMonths( skipped ) );
		this.skippedBox.textContent = __( 'You haven\'t chosen %s. Add them?' ).replace( '%s', range ) + ' ';
		this.skippedBox.appendChild( add );
		this.skippedBox.hidden = false;
	}

	/**
	 * A date from the server (`YYYY-MM-DD`) as a person reads it.
	 *
	 * @param {string} date The date.
	 * @return {string}
	 */
	readableDate( date ) {
		const [ year, month, day ] = String( date ).split( '-' ).map( ( piece ) => parseInt( piece, 10 ) );

		try {
			return new Intl.DateTimeFormat( document.documentElement.lang || undefined, { dateStyle: 'long', timeZone: 'UTC' } ).format( new Date( Date.UTC( year, month - 1, day, 12 ) ) );
		} catch ( error ) {
			return String( date );
		}
	}

	/**
	 * Chooses several months at once, those the grid has and the limit allows.
	 *
	 * @param {string[]} months `YYYY-MM` months.
	 * @return {void}
	 */
	addMonths( months ) {
		const have = new Set( this.selected.map( ( item ) => item.month ) );

		months.forEach( ( month ) => {
			if ( ! this.buttons.has( month ) || have.has( month ) ) {
				return;
			}

			if ( this.schema.max_months && this.selected.length >= Number( this.schema.max_months ) ) {
				return;
			}

			this.selected.push( {
				month,
				tierId: this.different ? this.wizard.selectedTierId : null,
				custom: this.different ? this.wizard.customAmount : null,
			} );
		} );
		this.selected.sort( ( a, b ) => a.month.localeCompare( b.month ) );
		this.sync();
		this.wizard.updateSummary();
	}

	/**
	 * What the monthly gift adds to a request: its amount choice, never a figure the server has not resolved.
	 *
	 * @return {Object}
	 */
	extras() {
		if ( 'both' !== this.mode ) {
			return {};
		}

		if ( null !== this.monthlyTierId ) {
			return { monthly_tier_id: this.monthlyTierId };
		}

		return null !== this.monthlyCustom ? { monthly_custom: this.monthlyCustom } : {};
	}

	/**
	 * The switch for a different amount in each month.
	 *
	 * @return {HTMLElement}
	 */
	buildDifferentToggle() {
		const label = document.createElement( 'label' );
		const input = document.createElement( 'input' );

		label.className = 'anudan-checkout__dated-different';
		input.type = 'checkbox';
		input.checked = false;
		input.addEventListener( 'change', () => {
			this.different = input.checked;
			this.selected.forEach( ( item ) => {
				item.tierId = this.different ? this.wizard.selectedTierId : null;
				item.custom = this.different ? this.wizard.customAmount : null;
			} );
			this.sync();
			this.wizard.updateSummary();
		} );
		label.appendChild( input );
		label.appendChild( document.createTextNode( ' ' + __( 'Give a different amount for each month' ) ) );

		return label;
	}

	/**
	 * Chooses or drops a month.
	 *
	 * @param {string} month `YYYY-MM`.
	 * @return {void}
	 */
	toggle( month ) {
		const at = this.selected.findIndex( ( item ) => item.month === month );

		if ( at >= 0 ) {
			this.selected.splice( at, 1 );
		} else {
			if ( this.schema.max_months && this.selected.length >= Number( this.schema.max_months ) ) {
				this.showErrors( { months: __( 'That is the most months one gift can cover.' ) } );

				return;
			}

			this.selected.push( {
				month,
				tierId: this.different ? this.wizard.selectedTierId : null,
				custom: this.different ? this.wizard.customAmount : null,
			} );
			this.selected.sort( ( a, b ) => a.month.localeCompare( b.month ) );
		}

		this.sync();
		this.wizard.updateSummary();
	}

	/**
	 * Draws everything that follows from the choice so far.
	 *
	 * @return {void}
	 */
	sync() {
		const monthsOn = 'every' !== this.mode;
		const chosen = new Set( this.selected.map( ( item ) => item.month ) );

		this.monthsPanel.hidden = ! monthsOn;
		this.listPanel.hidden = ! monthsOn;

		if ( this.differentPanel ) {
			this.differentPanel.hidden = ! monthsOn;
		}

		this.monthlyPanel.hidden = 'both' !== this.mode;

		if ( 'both' === this.mode ) {
			this.amountHeading.textContent = this.different ? __( 'Amount to start each of these months with' ) : __( 'Amount for each of these months' );
		} else {
			this.amountHeading.textContent = monthsOn
				? ( this.different ? __( 'Amount to start each month with' ) : __( 'Amount for every month' ) )
				: __( 'Monthly amount' );
		}

		this.buttons.forEach( ( button, month ) => {
			const on = chosen.has( month );

			button.setAttribute( 'aria-pressed', on ? 'true' : 'false' );
			button.classList.toggle( 'is-selected', on );
		} );

		( this.yearSummaries || new Map() ).forEach( ( { summary, months }, year ) => {
			const count = months.filter( ( month ) => chosen.has( month ) ).length;

			summary.textContent = count > 0
				? `${ year } · ${ count } ${ __( 'chosen' ) }`
				: `${ year } · ${ months.length } ${ 1 === months.length ? __( 'month' ) : __( 'months' ) }`;
		} );

		this.renderList();
		this.showErrors( {} );
	}

	/**
	 * The chosen months in order, each with its own amount when different amounts are on, and a way to take it off.
	 *
	 * @return {void}
	 */
	renderList() {
		this.listPanel.textContent = '';
		this.rows.clear();

		if ( ! this.selected.length ) {
			return;
		}

		const list = document.createElement( 'ul' );

		list.className = 'anudan-checkout__dated-rows';

		this.selected.forEach( ( item ) => {
			const row = document.createElement( 'li' );
			const name = document.createElement( 'span' );
			const remove = document.createElement( 'button' );

			row.className = 'anudan-checkout__dated-row';
			row.dataset.month = item.month;
			name.className = 'anudan-checkout__dated-row-name';
			name.textContent = monthLabel( item.month );
			row.appendChild( name );

			if ( this.different ) {
				row.appendChild( this.buildRowAmount( item ) );
			}

			remove.type = 'button';
			remove.className = 'anudan-checkout__dated-remove';
			remove.textContent = '×';
			remove.setAttribute( 'aria-label', __( 'Remove %s' ).replace( '%s', monthLabel( item.month ) ) );
			remove.addEventListener( 'click', () => this.toggle( item.month ) );
			row.appendChild( remove );

			this.rows.set( item.month, row );
			list.appendChild( row );
		} );

		this.listPanel.appendChild( list );
	}

	/**
	 * One month's own amount: the fund's amounts, or the donor's own.
	 *
	 * @param {{month: string, tierId: (number|null), custom: (number|null)}} item The month.
	 * @return {HTMLElement}
	 */
	buildRowAmount( item ) {
		const holder = document.createElement( 'span' );
		const select = document.createElement( 'select' );
		const money = document.createElement( 'span' );
		const input = document.createElement( 'input' );

		holder.className = 'anudan-checkout__dated-row-amount';
		select.className = 'anudan-checkout__dated-select';
		select.setAttribute( 'aria-label', __( 'Amount for %s' ).replace( '%s', monthLabel( item.month ) ) );

		( this.wizard.form.amounts || [] ).forEach( ( tier ) => {
			const option = document.createElement( 'option' );

			option.value = String( tier.id );
			option.textContent = this.wizard.formatMinorUnits( tier.amount );
			select.appendChild( option );
		} );

		if ( this.wizard.form.allow_custom_amount ) {
			const other = document.createElement( 'option' );

			other.value = 'other';
			other.textContent = __( 'Another amount' );
			select.appendChild( other );
		}

		const typed = null !== item.custom || null === item.tierId;

		select.value = typed ? 'other' : String( item.tierId );
		money.className = 'anudan-checkout__money';
		input.type = 'text';
		input.inputMode = 'decimal';
		input.className = 'anudan-checkout__field-input anudan-checkout__dated-input';
		input.setAttribute( 'aria-label', __( 'Your own amount for %s' ).replace( '%s', monthLabel( item.month ) ) );
		input.value = null !== item.custom ? String( ( item.custom / 100 ).toFixed( 2 ) ) : '';
		input.hidden = 'other' !== select.value;

		select.addEventListener( 'change', () => {
			if ( 'other' === select.value ) {
				item.tierId = null;
				input.hidden = false;
				input.focus();
			} else {
				item.tierId = parseInt( select.value, 10 );
				item.custom = null;
				input.hidden = true;
			}

			this.wizard.updateSummary();
		} );
		input.addEventListener( 'input', () => {
			item.tierId = null;
			item.custom = this.wizard.parseMinorUnits( input.value );
			this.wizard.updateSummary();
		} );

		holder.appendChild( select );
		money.appendChild( input );
		holder.appendChild( money );

		return holder;
	}

	/**
	 * The way of giving the server is asked about.
	 *
	 * @return {string}
	 */
	monthMode() {
		return this.mode;
	}

	/**
	 * The frequency this way of giving means: a gift paid once, or a monthly one.
	 *
	 * @return {string}
	 */
	frequency() {
		return 'specific' === this.mode ? 'one_time' : 'monthly';
	}

	/**
	 * The months the server resolves: each with its own amount choice only when different amounts are on, never a total.
	 *
	 * @return {Object[]}
	 */
	payload() {
		if ( 'every' === this.mode ) {
			return [];
		}

		return this.selected.map( ( item ) => {
			const out = { month: item.month };

			if ( this.different && null !== item.tierId ) {
				out.amount_tier_id = item.tierId;
			} else if ( this.different && null !== item.custom ) {
				out.custom_amount = item.custom;
			}

			return out;
		} );
	}

	/**
	 * Whether the donor has not yet chosen enough to give: no month where months are needed, or no amount to start from.
	 *
	 * @return {boolean}
	 */
	nothingChosen() {
		const needsMonths = 'every' !== this.mode;

		if ( needsMonths && ! this.selected.length ) {
			return true;
		}

		if ( this.different && needsMonths ) {
			return this.selected.some( ( item ) => null === item.tierId && null === item.custom && null === this.wizard.selectedTierId && null === this.wizard.customAmount );
		}

		if ( 'both' === this.mode && null === this.monthlyTierId && null === this.monthlyCustom ) {
			return true;
		}

		return null === this.wizard.selectedTierId && null === this.wizard.customAmount;
	}

	/**
	 * What to say when Continue is pressed with nothing chosen yet.
	 *
	 * @return {string}
	 */
	prompt() {
		if ( 'every' !== this.mode && ! this.selected.length ) {
			return __( 'Please choose at least one month first.' );
		}

		if ( 'both' === this.mode && null === this.monthlyTierId && null === this.monthlyCustom && ( this.different || null !== this.wizard.selectedTierId || null !== this.wizard.customAmount ) ) {
			return __( 'Please choose the monthly amount first.' );
		}

		return __( 'Please choose an amount first.' );
	}

	/**
	 * Shows what the server resolved: how many months and what they come to.
	 *
	 * @param {Object} body The quote's answer.
	 * @return {void}
	 */
	showResolved( body ) {
		this.showErrors( {} );
		this.showMonthly( body.monthly );

		if ( ! Array.isArray( body.months ) ) {
			return;
		}

		body.months.forEach( ( resolved ) => {
			const row = this.rows.get( resolved.month );

			if ( row ) {
				row.dataset.amount = this.wizard.formatMinorUnits( resolved.amount );
				row.title = this.wizard.formatMinorUnits( resolved.amount );
			}
		} );

		this.showNotices( body.notices );
	}

	/**
	 * Tells a signed-in donor, gently and without blocking anything, which months they already gave for and what their monthly gift covers.
	 *
	 * @param {Object|undefined} notices The quote's `notices`: `already_given` (months) and `covered_from` (a month).
	 * @return {void}
	 */
	showNotices( notices ) {
		if ( ! this.noticeBox ) {
			this.noticeBox = document.createElement( 'p' );
			this.noticeBox.className = 'anudan-checkout__dated-notice';
			this.noticeBox.setAttribute( 'role', 'status' );
			this.listPanel.parentNode.insertBefore( this.noticeBox, this.listPanel.nextSibling );
		}

		const sentences = [];
		const given = notices && Array.isArray( notices.already_given ) ? notices.already_given : [];

		if ( given.length ) {
			sentences.push( __( 'You have already given for %s. You can give for it again.' ).replace( '%s', given.map( monthLabel ).join( ', ' ) ) );
		}

		if ( notices && notices.covered_from ) {
			sentences.push( __( 'Your monthly gift already covers %s onward.' ).replace( '%s', monthLabel( notices.covered_from ) ) );
		}

		this.noticeBox.textContent = sentences.join( ' ' );
		this.noticeBox.hidden = ! sentences.length;
	}

	/**
	 * Says what the server refused, beside the months: in words, never a code.
	 *
	 * @param {Object<string, string>} fields The server's messages by field.
	 * @return {void}
	 */
	showErrors( fields ) {
		const messages = Object.keys( fields || {} ).filter( ( key ) => 'months' === key || key.startsWith( 'months.' ) || 'month_mode' === key || 'monthly_amount' === key ).map( ( key ) => fields[ key ] );

		this.errors.textContent = messages.join( ' ' );
		this.errors.hidden = ! messages.length;
	}
}
